import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Activity,
  Crosshair,
  Eye,
  Gauge,
  Grid3X3,
  Loader2,
  Radar,
  Target,
  TrendingUp,
  Trophy,
  Zap,
} from "lucide-react";

interface CreditPackage {
  id: "scan_credits_1000" | "scan_credits_5000";
  credits: number;
  price: string;
  pricePerCredit: string;
  scans: string;
  tagline: string;
  highlight?: string;
  features: string[];
  accent: "blue" | "orange";
}

const PACKAGES: CreditPackage[] = [
  {
    id: "scan_credits_1000",
    credits: 1000,
    price: "$40",
    pricePerCredit: "$0.040 / credit",
    scans: "≈ 5 full 13×13 GeoGrid scans",
    tagline: "Top up between scheduled scans",
    accent: "blue",
    features: [
      "1,000 GeoGrid pings added instantly to your account",
      "Run targeted scans on any keyword, any time",
      "Watch competitors move grid-by-grid in real time",
      "Confirm a ranking jump the moment it happens",
      "Credits never expire — burn them when you need them",
    ],
  },
  {
    id: "scan_credits_5000",
    credits: 5000,
    price: "$189",
    pricePerCredit: "$0.0378 / credit",
    scans: "≈ 29 full 13×13 GeoGrid scans",
    tagline: "Power-user pack — best value",
    highlight: "Save $11 vs the 1k pack",
    accent: "orange",
    features: [
      "5,000 GeoGrid pings added instantly to your account",
      "Stress-test new keywords across multiple cities",
      "Run weekly competitor sweeps for an entire quarter",
      "Build a deep history of map-pack movement",
      "Lowest cost per scan — optimized for serious operators",
    ],
  },
];

const WHY_IT_MATTERS = [
  {
    icon: Target,
    title: "Prove your ranking progress",
    body: "A single rank check is a snapshot. A grid scan is a heat map. The only way to actually see whether you're climbing the map pack — across the whole city, not just your office — is to scan, then scan again, then scan again.",
  },
  {
    icon: Eye,
    title: "Spy on the competition",
    body: "Every grid scan logs which competitors are showing up at every pin. Want to know who's stealing the top three in your best ZIP? Who's slipping? Who you just leapfrogged? You can't answer any of that without credits in the tank.",
  },
  {
    icon: TrendingUp,
    title: "Catch the wins (and the dips) early",
    body: "Google updates the map pack constantly. New citation just landed? New review streak? A weekly scan tells you exactly which grid points moved and by how much — so you know what's working before the leads even start to shift.",
  },
  {
    icon: Trophy,
    title: "Beat your competitors with data",
    body: "When you can show a client a side-by-side of last month's grid vs this month's grid — with their pins lighting up green and their competitor's pins fading — you don't have to sell them on staying. The map sells itself.",
  },
];

export default function ScanCreditsPage() {
  const { toast } = useToast();

  const checkout = useMutation({
    mutationFn: async (packageType: CreditPackage["id"]) => {
      const res = await apiRequest("POST", "/api/scan-credits/checkout", { packageType });
      return (await res.json()) as { url?: string };
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: "Checkout unavailable",
          description: "We couldn't start checkout. Please try again in a moment.",
          variant: "destructive",
        });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Checkout failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8 space-y-12" data-testid="page-scan-credits">
      {/* Hero */}
      <div className="text-center space-y-4">
        <Badge
          variant="outline"
          className="border-[#0081DF]/40 text-[#0081DF] bg-[#0081DF]/5"
          data-testid="badge-scan-credits-hero"
        >
          <Radar className="h-3.5 w-3.5 mr-1.5" />
          GeoGrid Scan Credits
        </Badge>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight"
          data-testid="text-scan-credits-headline"
        >
          You can't dominate what you can't <span className="text-[#F6642A]">measure</span>.
        </h1>
        <p
          className="text-lg text-muted-foreground max-w-3xl mx-auto"
          data-testid="text-scan-credits-subhead"
        >
          Scan credits power every GeoGrid ping — the heat-map scans that prove your rank in the
          map pack, expose what your competitors are doing, and let you watch your domination
          unfold one grid point at a time.
        </p>
      </div>

      {/* Pricing cards — placed up top so the buy options are visible immediately */}
      <section className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold" data-testid="text-pricing-title">
            Choose your pack
          </h2>
          <p className="text-sm text-muted-foreground">
            One-time purchases. Credits land in your account immediately after checkout.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PACKAGES.map((pkg) => {
            const isOrange = pkg.accent === "orange";
            const accentColor = isOrange ? "#F6642A" : "#0081DF";
            return (
              <Card
                key={pkg.id}
                className="relative flex flex-col border-2 hover-elevate"
                style={{ borderColor: `${accentColor}33` }}
                data-testid={`card-package-${pkg.id}`}
              >
                {pkg.highlight && (
                  <Badge
                    className="absolute -top-3 right-4 text-white border-0"
                    style={{ backgroundColor: accentColor }}
                    data-testid={`badge-highlight-${pkg.id}`}
                  >
                    {pkg.highlight}
                  </Badge>
                )}
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-12 w-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${accentColor}1a` }}
                      >
                        {isOrange ? (
                          <Crosshair className="h-6 w-6" style={{ color: accentColor }} />
                        ) : (
                          <Activity className="h-6 w-6" style={{ color: accentColor }} />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-2xl" data-testid={`text-credits-${pkg.id}`}>
                          {pkg.credits.toLocaleString()} Credits
                        </CardTitle>
                        <CardDescription>{pkg.tagline}</CardDescription>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3 pt-4">
                    <div
                      className="text-3xl sm:text-4xl font-bold"
                      style={{ color: accentColor }}
                      data-testid={`text-price-${pkg.id}`}
                    >
                      {pkg.price}
                    </div>
                    <div className="text-sm text-muted-foreground">{pkg.pricePerCredit}</div>
                  </div>
                  <div className="text-sm text-muted-foreground" data-testid={`text-scans-${pkg.id}`}>
                    {pkg.scans}
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-2.5">
                    {pkg.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm"
                        data-testid={`feature-${pkg.id}-${i}`}
                      >
                        <span
                          className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: `${accentColor}26` }}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: accentColor }}
                          />
                        </span>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <button
                    type="button"
                    onClick={() => checkout.mutate(pkg.id)}
                    disabled={checkout.isPending}
                    className="inline-flex items-center justify-center w-full h-10 px-4 rounded-md text-sm font-medium text-white border-0 hover-elevate active-elevate-2 transition-colors disabled:opacity-60"
                    style={{ backgroundColor: accentColor }}
                    data-testid={`button-buy-${pkg.id}`}
                  >
                    {checkout.isPending && checkout.variables === pkg.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    Buy {pkg.credits.toLocaleString()} credits
                  </button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground">
          Secure checkout via Stripe. Credits are added to your account automatically once your
          payment clears.
        </p>
      </section>

      {/* Why credits matter */}
      <section className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold" data-testid="text-why-credits-title">
            Why scan credits matter
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Every credit is one map-pack ranking check at one grid point. The more credits you
            burn, the clearer the picture of what's actually happening in your local market.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {WHY_IT_MATTERS.map((item) => {
            const Icon = item.icon;
            return (
              <Card
                key={item.title}
                className="border-l-4 border-l-[#F6642A]"
                data-testid={`card-why-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#F6642A]/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#F6642A]" />
                    </div>
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Mechanics strip */}
      <section className="rounded-xl border bg-muted/30 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="space-y-2" data-testid="stat-credits-per-scan">
            <Grid3X3 className="h-6 w-6 text-[#0081DF] mx-auto" />
            <div className="text-3xl font-bold">169</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Credits per full 13×13 scan
            </div>
          </div>
          <div className="space-y-2" data-testid="stat-instant-grant">
            <Zap className="h-6 w-6 text-[#F6642A] mx-auto" />
            <div className="text-3xl font-bold">Instant</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Credits added the moment payment clears
            </div>
          </div>
          <div className="space-y-2" data-testid="stat-no-expiry">
            <Gauge className="h-6 w-6 text-[#0081DF] mx-auto" />
            <div className="text-3xl font-bold">No expiry</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Burn credits at your own pace
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
